import { useEffect, useState } from "react";
import useSWR from "swr";
import { hvoAuth, hvoFirestore, vceFirestore } from "../../configs/FirebaseConfig";
import { FIRESTORE_ORDERS_TABLE } from "../../constants/FirestoreConstant";
import { ORDER_STATUS_CREATED } from "../../constants/OrderConstant";
import { PICK_DAY_GAP_DAYS } from "../../constants/PickingConstant";
import { deliveryDatesForPickDay } from "../../functions/pickDay";
import { hasHvoSession } from "./useProductFields";

/**
 * The orders of one pick day, from both shops.
 *
 * Deliberately not the orders page's `useOrders`: that one is keyed on the selected delivery
 * date and patched optimistically, while a pick day can cover several delivery dates (Friday
 * with Saturday, and more once there is a day in between). Each shop is read on its own, so a
 * failing HVO read shows as "HVO not loaded" rather than as a day without HVO orders.
 *
 * The HVO session is not there from the first render: the login signs in to VCE first, and the
 * auth pages already move on to the order overview before the HVO login is done (on a reload
 * Firebase restores both sessions asynchronously). A read without that session is refused by the
 * rules, so it is not sent: HVO is reported as not loaded, and the hook reads again as soon as
 * the session arrives.
 */

const SHOPS = [
  // The VCE session is guaranteed here: the CMS routes only render once it exists
  { shopSource: "VCE", shopName: "Vleescentrum", firestore: () => vceFirestore, hasSession: () => true },
  { shopSource: "HVO", shopName: "Halalvlees Online", firestore: () => hvoFirestore, hasSession: hasHvoSession },
];

export const NO_SESSION = Object.freeze({ code: "no-session", message: "No session for this shop" });

export const fetchOrdersForDeliveryDates = async (dates) => {
  if (!dates || dates.length === 0) return { orders: [], loadedShops: [], errorsByShop: {} };
  const results = await Promise.all(
    SHOPS.map(async ({ shopSource, shopName, firestore, hasSession }) => {
      if (!hasSession()) return { shopSource, orders: [], error: NO_SESSION };
      try {
        const snapshot = await firestore()
          .collection(FIRESTORE_ORDERS_TABLE)
          .where("delivery.date", "in", dates)
          .where("orderStatus", "!=", ORDER_STATUS_CREATED)
          .get();
        const orders = snapshot.docs
          // HVO holds copies of VCE orders marked isVleescentrum; the orders page drops them too
          .filter((doc) => shopSource !== "HVO" || !doc.data().isVleescentrum)
          .map((doc) => ({ id: doc.id, ...doc.data(), shopSource, shopName }));
        return { shopSource, orders };
      } catch (error) {
        console.error(`[usePickDayOrders] ${shopSource} orders could not be read:`, error);
        return { shopSource, orders: [], error };
      }
    }),
  );
  return {
    orders: results.flatMap((result) => result.orders).sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0)),
    loadedShops: results.filter((result) => !result.error).map((result) => result.shopSource),
    errorsByShop: results.reduce((errors, result) => (result.error ? { ...errors, [result.shopSource]: result.error } : errors), {}),
  };
};

const EMPTY = { orders: [], loadedShops: [], errorsByShop: {} };

/** The uid of the HVO session, following it as it signs in, restores or signs out. */
const useHvoUid = () => {
  const [uid, setUid] = useState(() => (hvoAuth.currentUser ? hvoAuth.currentUser.uid : null));
  useEffect(() => hvoAuth.onAuthStateChanged((user) => setUid(user ? user.uid : null)), []);
  return uid;
};

export const usePickDayOrders = (pickDay, { gapDays = PICK_DAY_GAP_DAYS } = {}) => {
  const dates = pickDay ? deliveryDatesForPickDay(pickDay, { gapDays }) : [];
  // Part of the key, so the day is read again once the HVO session is there
  const hvoUid = useHvoUid();
  const { data, error, isLoading, mutate } = useSWR(
    pickDay ? ["pickDayOrders", pickDay, gapDays, hvoUid] : null,
    () => fetchOrdersForDeliveryDates(dates),
    // No keepPreviousData: after switching days it would show the previous day's counts as loaded
    { refreshInterval: 30000, revalidateOnFocus: true, dedupingInterval: 5000 },
  );
  const result = data || EMPTY;
  return {
    orders: result.orders,
    loadedShops: result.loadedShops,
    errorsByShop: result.errorsByShop,
    deliveryDates: dates,
    isLoading,
    isLoaded: !!data,
    error,
    refresh: mutate,
  };
};
